import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema } from './common.js';
import { dataSourceSchema, publicationFormatSchema } from './enums.js';
/**
 * Normalised shape every adapter returns, so the rest of the app never sees
 * the differences between Metron, the GCD, a library catalogue and Google.
 */
export const sourceIssueSchema = z.object({
    source: dataSourceSchema,
    externalId: z.string(),
    url: z.url().nullable(),
    seriesName: z.string(),
    seriesExternalId: z.string().nullable(),
    publisherName: z.string().nullable(),
    language: z.string().nullable(),
    format: publicationFormatSchema.nullable(),
    number: z.string(),
    title: z.string().nullable(),
    variantName: z.string().nullable(),
    releaseDate: isoDateSchema.nullable(),
    coverDate: isoDateSchema.nullable(),
    price: z.number().nullable(),
    currency: z.string().nullable(),
    pageCount: z.number().int().nullable(),
    description: z.string().nullable(),
    isbn: z.string().nullable(),
    upc: z.string().nullable(),
    coverUrl: z.url().nullable(),
    /**
     * The source the cover came from, when it is not the issue's own source:
     * a hit without a cover asks the later sources for one (Open Library knows
     * a book, Google Books has its cover). Downloads go to that source's hosts.
     */
    coverSource: dataSourceSchema.nullish(),
    creators: z.array(z.object({ name: z.string(), role: z.string() })),
    /** Required attribution, e.g. for CC BY-SA sources. */
    attribution: z.string().nullable(),
});
export const sourceSeriesSchema = z.object({
    source: dataSourceSchema,
    externalId: z.string(),
    url: z.url().nullable(),
    name: z.string(),
    volume: z.string().nullable(),
    startYear: z.number().int().nullable(),
    endYear: z.number().int().nullable(),
    publisherName: z.string().nullable(),
    language: z.string().nullable(),
    format: publicationFormatSchema.nullable(),
    /** Total number of issues the source knows about. */
    issueCount: z.number().int().nullable(),
    description: z.string().nullable(),
    coverUrl: z.url().nullable(),
    attribution: z.string().nullable(),
});
// ------------------------------------------------------------- settings
export const sourceCapabilitiesSchema = z.object({
    barcode: z.boolean(),
    isbn: z.boolean(),
    search: z.boolean(),
    seriesIssues: z.boolean(),
});
export const sourceConfigSchema = z.object({
    source: dataSourceSchema,
    enabled: z.boolean(),
    /** Lower runs first. */
    order: z.number().int().min(0).max(100),
    /** Secret values are never returned; the API reports whether one is stored. */
    hasCredentials: z.boolean(),
    credentialLabel: z.string().nullable(),
    cacheTtlHours: z.number().int().min(1).max(8760),
});
export const sourceStatusSchema = sourceConfigSchema.extend({
    label: z.string(),
    description: z.string(),
    capabilities: sourceCapabilitiesSchema,
    /** False when the source needs credentials it does not have. */
    ready: z.boolean(),
    requiresCredentials: z.boolean(),
    docsUrl: z.url(),
    attribution: z.string().nullable(),
    lastCheckedAt: isoDateTimeSchema.nullable(),
    lastError: z.string().nullable(),
    options: z.array(z.object({
        key: z.string(),
        label: z.string(),
        description: z.string(),
        value: z.boolean(),
    })),
});
export const sourceSettingsUpdateSchema = z.object({
    enabled: z.boolean().optional(),
    order: z.number().int().min(0).max(100).optional(),
    cacheTtlHours: z.number().int().min(1).max(8760).optional(),
    /** Empty string clears the stored credential. */
    credential: z.string().max(500).optional(),
    options: z.record(z.string(), z.boolean()).optional(),
});
// -------------------------------------------------------------- lookups
export const lookupRequestSchema = z.object({
    code: z.string().trim().min(1).max(40),
    addon: z.string().trim().max(5).optional(),
    /** Ask the sources even when the series is already known locally. */
    forceRemote: z.boolean().default(false),
});
export const lookupMatchSchema = z.object({
    issue: sourceIssueSchema,
    /** Already in the collection: the local issue it matched. */
    localIssueId: idSchema.nullable(),
    ownedQuantity: z.number().int().nonnegative(),
});
export const lookupResponseSchema = z.object({
    /** How the code was read. */
    barcode: z.object({
        normalized: z.string(),
        addon: z.string().nullable(),
        kind: z.string(),
        checkDigitValid: z.boolean(),
    }),
    /** Local hit: the issue is already catalogued. */
    local: z
        .object({
        issueId: idSchema,
        seriesId: idSchema,
        seriesTitle: z.string(),
        number: z.string(),
        ownedQuantity: z.number().int().nonnegative(),
        /** For "photograph the cover" when the issue has none yet. */
        coverImageId: idSchema.nullable(),
    })
        .nullable(),
    /**
     * Same code, different add-on: the series is known locally but not this
     * issue. Happens when a US book is scanned without its EAN-5, so the user
     * picks the number from these.
     */
    localCandidates: z.array(z.object({
        issueId: idSchema,
        seriesId: idSchema,
        seriesTitle: z.string(),
        number: z.string(),
        variantName: z.string().nullable(),
        coverImageId: idSchema.nullable(),
        ownedQuantity: z.number().int().nonnegative(),
    })),
    matches: z.array(lookupMatchSchema),
    /** Sources that were asked and what came back. */
    searched: z.array(z.object({
        source: dataSourceSchema,
        status: z.enum(['hit', 'miss', 'skipped', 'error', 'cached']),
        message: z.string().nullable(),
    })),
});
export const seriesSearchResponseSchema = z.object({
    results: z.array(sourceSeriesSchema),
    searched: z.array(z.object({
        source: dataSourceSchema,
        status: z.enum(['hit', 'miss', 'skipped', 'error', 'cached']),
        message: z.string().nullable(),
    })),
});
export const importSeriesRequestSchema = z.object({
    source: dataSourceSchema,
    externalId: z.string().min(1).max(100),
    /** Create issue records for the whole run, the basis of the gap view. */
    includeIssues: z.boolean().default(true),
    /** Download covers for the issues as well. */
    includeCovers: z.boolean().default(false),
});
export const importSeriesResponseSchema = z.object({
    seriesId: idSchema,
    created: z.boolean(),
    issuesCreated: z.number().int(),
    issuesUpdated: z.number().int(),
    issuesSkipped: z.number().int(),
    coversDownloaded: z.number().int(),
    protectedFields: z.array(z.string()),
    /** Set when part of the import failed, e.g. the source throttled us. */
    warning: z.string().nullable(),
});
export const completeSeriesResponseSchema = z.object({
    source: dataSourceSchema.nullable(),
    result: importSeriesResponseSchema.nullable(),
    message: z.string().nullable(),
});
export const refreshRequestSchema = z.object({
    /** Limit the refresh to one source; otherwise the configured order is used. */
    source: dataSourceSchema.optional(),
    includeCover: z.boolean().default(false),
});
export const refreshResponseSchema = z.object({
    source: dataSourceSchema.nullable(),
    updatedFields: z.array(z.string()),
    /** Fields left untouched because they are manual overrides. */
    protectedFields: z.array(z.string()),
    coverDownloaded: z.boolean(),
    message: z.string().nullable(),
});
// ------------------------------------------------------------ scan flow
export const acceptMatchRequestSchema = z.object({
    /** The match the user picked, exactly as the lookup returned it. */
    issue: sourceIssueSchema,
    /** The scanned code, so the next scan of it hits locally. */
    code: z.string().trim().min(1).max(40),
    addon: z.string().trim().max(5).nullish(),
    addToCollection: z.boolean().default(true),
    downloadCover: z.boolean().default(true),
    location: z.string().trim().max(120).nullish(),
});
export const acceptMatchResponseSchema = z.object({
    seriesId: idSchema,
    issueId: idSchema,
    collectionItemId: idSchema.nullable(),
    createdSeries: z.boolean(),
    createdIssue: z.boolean(),
    coverImageId: idSchema.nullable(),
    /** The rest of the run is being loaded in the background. */
    seriesRunQueued: z.boolean(),
});
export const attachBarcodeRequestSchema = z.object({
    code: z.string().trim().min(1).max(40),
    addon: z.string().trim().max(5).nullish(),
});
// ---------------------------------------------------------- batch scanning
/**
 * One scan from the phone's batch scanner. Every scan carries an id made on
 * the device, so sending it twice (flaky connection, offline queue) never
 * adds a second copy.
 */
export const batchScanItemSchema = z.object({
    clientScanId: z.uuid(),
    code: z.string().trim().min(1).max(40),
    addon: z.string().trim().max(5).nullish(),
    /** When it was scanned on the device, which may be long before syncing. */
    scannedAt: isoDateTimeSchema,
});
export const batchScanRequestSchema = z.object({
    scans: z.array(batchScanItemSchema).min(1).max(50),
    /** Shelf or box the whole stack goes to. */
    location: z.string().trim().max(120).nullish(),
});
/**
 * What became of a scan:
 * - added: a copy is in the collection now
 * - owned: already owned; nothing added, the user can add another copy
 * - needs_choice: several matches or a US code without add-on; waits in
 *   the scan queue for a decision
 * - not_found: no source knows it; waits in the scan queue
 * - pending: the same scan is being processed right now; ask again later
 */
export const batchScanStatusSchema = z.enum([
    'added',
    'owned',
    'needs_choice',
    'not_found',
    'pending',
]);
export const batchScanOutcomeSchema = z.object({
    clientScanId: z.uuid(),
    status: batchScanStatusSchema,
    /** "Deadly Class #1", when known. */
    label: z.string().nullable(),
    seriesId: idSchema.nullable(),
    issueId: idSchema.nullable(),
    coverImageId: idSchema.nullable(),
    seriesRunQueued: z.boolean(),
});
export const batchScanResponseSchema = z.object({
    results: z.array(batchScanOutcomeSchema),
});
//# sourceMappingURL=sources.js.map